import { createTheme, alpha } from '@mui/material/styles';


export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0a192f',
      light: '#172a45',
      dark: '#020c1b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#bb4d00',
      contrastText: '#000000',
    },
    accent: {
      main: '#4b6584',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f7fa',  // Light, soft background
      paper: '#ffffff',    // Cards/panels remain clean
    },
    text: {
      primary: '#0a192f',  // Navy as main text
      secondary: '#495057', // Muted gray
    },
    divider: '#dee2e6',
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.75rem',
      color: '#0a192f',
      '@media (max-width:600px)': {
        fontSize: '1.75rem', // Shrinks on mobile
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#0a192f',
      '@media (max-width:600px)': {
      fontSize: '1.4rem',
    },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#172a45',
    },
    h6: {
      fontWeight: 500,
      color: '#020c1b',
    },
    body1: {
      fontSize: '1rem',
      color: '#495057',
      '@media (max-width:600px)': {
      fontSize: '1rem',
    },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  transitions: {
    duration: {
      standard: 250,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#f5f7fa',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.secondary.main, 0.12),
            color: theme.palette.secondary.main,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            padding: '10px 16px',
            fontSize: '0.95rem',
          },
        }),
        containedPrimary: {
          backgroundColor: '#0a192f',
          '&:hover': {
            backgroundColor: '#172a45',
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: '#ffa726',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a192f',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: 4
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            boxShadow: 'none',    // Remove heavy shadows on small screens
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minWidth: 80,         // Prevent tabs from becoming too wide
            fontSize: '0.8rem',   // Scale down text slightly
            padding: '6px 12px',
          },
        }),
      }
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          padding: 2,
          [theme.breakpoints.down('sm')]: {
            borderRadius: 0,      // Square edges look more "native" on mobile
            borderLeft: 'none',   // Remove side borders to save pixels
            borderRight: 'none',
            boxShadow: 'none',    // Flatter UI feels lighter on mobile
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 12, 
          '&:last-child': { paddingBottom: 24 },
          [theme.breakpoints.down('sm')]: {
            padding: 0,          // Reduced from 24 to 12
            '&:last-child': { paddingBottom: 12 },
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            width: '100%', // Mobile: Big touch targets
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#f1f3f5',
          color: '#0a192f',
        },
        input: {
          color: '#0a192f',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#ced4da',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      }
    }
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#0a192f',
      light: '#172a45',
      dark: '#020c1b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#bb4d00',
      contrastText: '#000000',
    },
    accent: {
      main: '#4b6584',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0a192f',  // Deep navy base
      paper: '#172a45',    // Slightly lifted card color
    },
    text: {
      primary: '#ffffff',
      secondary: '#aab8c2',
    },
    divider: '#1e2a3a',
    warning: {
      main: '#ff9800',
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.75rem',
      color: '#0a192f',
      '@media (max-width:600px)': {
          fontSize: '1.75rem', // Shrinks on mobile
      },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#0a192f',
      '@media (max-width:600px)': {
        fontSize: '1.4rem',
    },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#e0e6ed',
    },
    h6: {
      fontWeight: 500,
      color: '#aab8c2',
    },
    body1: {
      fontSize: '1rem',
      color: '#aab8c2',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 4,
  },
  spacing: 8,
  transitions: {
    duration: {
      standard: 250,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a192f',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            width: '100%',
          },
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            boxShadow: 'none',    // Remove heavy shadows on small screens
          },
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minWidth: 80,         // Prevent tabs from becoming too wide
            fontSize: '0.8rem',   // Scale down text slightly
            padding: '6px 12px',
          },
        }),
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a192f',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        },
      },
    },
   
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          padding: 2,
          [theme.breakpoints.down('sm')]: {
            borderRadius: 2,      // Square edges look more "native" on mobile
            borderLeft: 'none',   // Remove side borders to save pixels
            borderRight: 'none',
            boxShadow: 'none',    // Flatter UI feels lighter on mobile
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 12, 
          '&:last-child': { paddingBottom: 24 },
          [theme.breakpoints.down('sm')]: {
            padding: 2,         
            '&:last-child': { paddingBottom: 12 },
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f2238',
          color: '#ffffff',
        },
        input: {
          color: '#ffffff',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#33475b',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true
      }
    }
  },
});


